/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { FlexContainer, SVGIcon, theme } from "@nordcloud/gnui";
import {
  ActionWithMissingDataErrorCode,
  OsType,
  SortOrder,
} from "~/generated/graphql";
import { dateFormat } from "~/constants";
import { useSearchState } from "~/hooks/useSearchState";
import { useResourceComplianceReportForUI } from "~/views/resources/hooks";
import { complianceStatusDetails } from "../../const";
import type { ComplianceStatus } from "../../types";
import { getUpdateReportStatusDisplay } from "../const";
import {
  type ActionWithMissingData,
  UpdateReportItemStatus,
  type UpdateReportOverviewData,
  type UpdateReportTableState,
  type UpdateReportSortField,
  type UpdateReportDetail,
} from "../types";
import { isNaLike, getUpdateReportStatusChartItems } from "../utils";

const GENERATING_MESSAGE_DELAY_MS = 2000;
const REPORTS_PER_PAGE = 10;

const normalizeUpdateStatusKey = (value: string) =>
  value.replace(/[\s_-]/g, "").toLowerCase();

const humanizeUpdateStatusLabel = (value: string) => {
  const spaced = value
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  return spaced.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const updateStatusKeyToItemStatus: Record<string, UpdateReportItemStatus> = {
  updated: UpdateReportItemStatus.UPDATED,
  installed: UpdateReportItemStatus.INSTALLED,
  installedpendingreboot: UpdateReportItemStatus.INSTALLED_PENDING_REBOOT,
  failed: UpdateReportItemStatus.FAILED,
  removed: UpdateReportItemStatus.REMOVED,
  available: UpdateReportItemStatus.AVAILABLE,
};

type ResourceInfo = {
  id: string;
  name?: string | null;
  os?: string | null;
};

const dedupeActionsWithMissingData = (
  actions: ActionWithMissingData[]
): ActionWithMissingData[] => {
  const dedupedByKey = actions.reduce<Record<string, ActionWithMissingData>>(
    (acc, action) => {
      const actionKey = `${action.eventId}:${action.eventActionId}:${action.errCode}`;

      if (acc[actionKey]) {
        return acc;
      }

      return {
        ...acc,
        [actionKey]: action,
      };
    },
    {}
  );

  return Object.values(dedupedByKey);
};

const mapPackageToDetail = (
  pkg: {
    eventActionId?: string | null;
    eventId?: string | null;
    updateName: string;
    updateStatus: string;
    time?: string | null;
    patchPolicy?: string | null;
    rebootAllowed?: string | null;
    releaseDate?: string | null;
    category?: string | null;
  },
  resourceId: string
) => {
  const normalizedKey = normalizeUpdateStatusKey(pkg.updateStatus);
  const mappedStatus =
    updateStatusKeyToItemStatus[normalizedKey] ??
    UpdateReportItemStatus.AVAILABLE;
  const statusLabel = updateStatusKeyToItemStatus[normalizedKey]
    ? getUpdateReportStatusDisplay(mappedStatus).label
    : humanizeUpdateStatusLabel(pkg.updateStatus);

  return {
    id: pkg.eventActionId ?? pkg.eventId ?? pkg.updateName,
    name: pkg.updateName,
    resourceId,
    status: mappedStatus,
    statusLabel,
    time: pkg.time ?? null,
    patchPolicy: pkg.patchPolicy,
    rebootAllowed: (() => {
      const value = pkg.rebootAllowed?.trim().toLowerCase();
      if (value === "true") {
        return true;
      }
      if (value === "false") {
        return false;
      }
      return null;
    })(),
    releaseDate: pkg.releaseDate ?? null,
    category: pkg.category ?? null,
    eventId: pkg.eventId ?? null,
    eventActionId: pkg.eventActionId ?? null,
  };
};

const buildCompare = (
  field: UpdateReportSortField,
  dir: SortOrder
): ((a: UpdateReportDetail, b: UpdateReportDetail) => number) => {
  const direction = dir === SortOrder.Asc ? 1 : -1;
  const missingRank = direction;

  const isMissingText = (value?: string | null) => isNaLike(value);

  const parseDateValue = (value?: string | null) => {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.valueOf() : null;
  };

  const compareWithMissing = <T,>({
    aValue,
    bValue,
    isMissing,
    compare,
  }: {
    aValue: T | null | undefined;
    bValue: T | null | undefined;
    isMissing: (value: T | null | undefined) => boolean;
    compare: (a: T, b: T) => number;
  }) => {
    const aMissing = isMissing(aValue);
    const bMissing = isMissing(bValue);
    if (aMissing && bMissing) {
      return 0;
    }
    if (aMissing) {
      return missingRank;
    }
    if (bMissing) {
      return -missingRank;
    }
    return direction * compare(aValue as T, bValue as T);
  };

  return (a, b) => {
    switch (field) {
      case "name":
        return direction * a.name.localeCompare(b.name);
      case "time":
        return compareWithMissing({
          aValue: parseDateValue(a.time),
          bValue: parseDateValue(b.time),
          isMissing: (value) => value == null,
          compare: (left, right) => left - right,
        });
      case "patchPolicy":
        return compareWithMissing({
          aValue: a.patchPolicy,
          bValue: b.patchPolicy,
          isMissing: isMissingText,
          compare: (left, right) => left.localeCompare(right),
        });
      case "rebootAllowed":
        return compareWithMissing({
          aValue: a.rebootAllowed,
          bValue: b.rebootAllowed,
          isMissing: (value) => value == null,
          compare: (left, right) => Number(left) - Number(right),
        });
      case "releaseDate":
        return compareWithMissing({
          aValue: parseDateValue(a.releaseDate),
          bValue: parseDateValue(b.releaseDate),
          isMissing: (value) => value == null,
          compare: (left, right) => left - right,
        });
      case "category":
        return compareWithMissing({
          aValue: a.category,
          bValue: b.category,
          isMissing: isMissingText,
          compare: (left, right) => left.localeCompare(right),
        });
      case "status":
        return direction * a.status.localeCompare(b.status);
      default:
        return 0;
    }
  };
};

export function useComplianceReport({
  resourceId,
  complianceStatus,
  resource,
}: {
  resourceId: string;
  complianceStatus: ComplianceStatus;
  resource?: ResourceInfo;
}) {
  const { searchState, updateSearchState } = useSearchState({
    sort: {
      field: "name",
      order: SortOrder.Asc,
    },
    limit: REPORTS_PER_PAGE,
  });
  const [selectedStatuses, setSelectedStatuses] = useState<
    UpdateReportItemStatus[]
  >([]);

  const reportPeriod = useMemo(() => {
    const start = dayjs.utc().startOf("month");
    const end = dayjs.utc().endOf("month");
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      formatted: `${start.format(dateFormat.dayMonthYear)} - ${end.format(
        dateFormat.dayMonthYear
      )}`,
    };
  }, []);

  const {
    packages,
    actionsWithMissingData,
    status,
    loading: complianceLoading,
    error,
    isPolling,
    timedOut,
    isFailed,
    retry,
  } = useResourceComplianceReportForUI({
    resourceId,
    startDate: reportPeriod.startDate,
    endDate: reportPeriod.endDate,
  });

  const [showGeneratingMessage, setShowGeneratingMessage] = useState(false);
  const isLoading = complianceLoading || isPolling;

  useEffect(() => {
    if (!isLoading) {
      setShowGeneratingMessage(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      setShowGeneratingMessage(true);
    }, GENERATING_MESSAGE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const derivedUpdates: UpdateReportDetail[] = useMemo(() => {
    return packages.map((item) => mapPackageToDetail(item, resourceId));
  }, [packages, resourceId]);

  const actionsMissingData: ActionWithMissingData[] = useMemo(() => {
    return dedupeActionsWithMissingData(actionsWithMissingData);
  }, [actionsWithMissingData]);

  const actionsWithMissingDataByErrCode = useMemo(() => {
    return actionsMissingData.reduce<
      Partial<Record<ActionWithMissingDataErrorCode, ActionWithMissingData[]>>
    >((groupedActions, action) => {
      const actionsForErrCode = groupedActions[action.errCode] ?? [];

      return {
        ...groupedActions,
        [action.errCode]: [...actionsForErrCode, action],
      };
    }, {});
  }, [actionsMissingData]);

  const phrase = useMemo(
    () => searchState.filter?.phrase?.trim().toLowerCase() ?? "",
    [searchState.filter?.phrase]
  );

  const sortField = (searchState.sort?.field ??
    "name") as UpdateReportSortField;
  const sortDir = searchState.sort?.order ?? SortOrder.Asc;
  const compare = useMemo(
    () => buildCompare(sortField, sortDir),
    [sortDir, sortField]
  );

  const filteredAndSortedUpdates = useMemo(() => {
    const { page = 1, limit = REPORTS_PER_PAGE } = searchState;

    const filtered = derivedUpdates.filter((item) => {
      const statusMatch =
        selectedStatuses.length === 0 || selectedStatuses.includes(item.status);
      const phraseMatch = phrase
        ? item.name.toLowerCase().includes(phrase)
        : true;
      return statusMatch && phraseMatch;
    });

    const sorted = filtered.reduce<UpdateReportDetail[]>((acc, item) => {
      const insertIndex = acc.findIndex(
        (existing) => compare(item, existing) < 0
      );
      if (insertIndex === -1) {
        return [...acc, item];
      }
      return [...acc.slice(0, insertIndex), item, ...acc.slice(insertIndex)];
    }, []);

    const start = (page - 1) * limit;
    const end = start + limit;

    return {
      updates: sorted.slice(start, end),
      total: sorted.length,
    };
  }, [compare, derivedUpdates, phrase, searchState, selectedStatuses]);

  const searchPhrase = searchState.filter?.phrase;
  const tableState: UpdateReportTableState = useMemo(
    () => ({
      search: searchPhrase,
      sortField: (searchState.sort?.field ?? "name") as UpdateReportSortField,
      sortDirection: searchState.sort?.order ?? SortOrder.Asc,
      page: searchState.page,
      limit: searchState.limit,
    }),
    [searchPhrase, searchState.limit, searchState.page, searchState.sort]
  );

  const detailsItems = useMemo(() => {
    const osLabel =
      resource?.os != null
        ? resource.os.charAt(0) + resource.os.slice(1).toLowerCase()
        : "—";
    const complianceDetails = complianceStatusDetails[complianceStatus];

    return [
      {
        label: "Resource Name",
        value: resource?.name ?? "—",
      },
      {
        label: "Resource ID",
        value: resource?.id,
      },
      {
        label: "Compliance",
        value: (
          <FlexContainer
            style={{
              gap: theme.spacing.spacing02,
              alignItems: "center",
            }}
          >
            {complianceDetails.icon ? (
              <SVGIcon
                name={complianceDetails.icon}
                color={complianceDetails.iconColor}
                size="md"
              />
            ) : null}
            {complianceDetails.label}
          </FlexContainer>
        ),
      },
      { label: "Operating System", value: osLabel },
      { label: "Report Period", value: reportPeriod.formatted },
    ];
  }, [complianceStatus, reportPeriod.formatted, resource]);

  const derivedOverview: UpdateReportOverviewData = useMemo(() => {
    const summary = {
      totalUpdates: derivedUpdates.length,
      byStatus: Object.values(UpdateReportItemStatus).map((itemStatus) => ({
        status: itemStatus,
        count: derivedUpdates.filter((item) => item.status === itemStatus)
          .length,
      })),
    };

    const osType: OsType =
      resource?.os && Object.values(OsType).includes(resource.os as OsType)
        ? (resource.os as OsType)
        : OsType.Linux;

    return {
      resource: {
        id: resource?.id ?? resourceId,
        name: resource?.name ?? "—",
        os: osType,
        calculationPeriod: {
          start: reportPeriod.startDate,
          end: reportPeriod.endDate,
        },
      },
      summary,
      filterSummary: {
        statusCounts: derivedUpdates.reduce(
          (acc, item) => ({
            ...acc,
            [item.status]: (acc[item.status] ?? 0) + 1,
          }),
          {} as Record<UpdateReportItemStatus, number>
        ),
        categoryCounts: {},
        patchPolicyCounts: {},
        rebootAllowedCounts: { yes: 0, no: 0 },
      },
    };
  }, [
    derivedUpdates,
    reportPeriod.endDate,
    reportPeriod.startDate,
    resource?.id,
    resource?.name,
    resource?.os,
    resourceId,
  ]);

  const updateStatusChartItems = useMemo(
    () => getUpdateReportStatusChartItems(derivedOverview),
    [derivedOverview]
  );

  return {
    reportPeriod,
    status,
    isLoading,
    isFailed,
    timedOut,
    showGeneratingMessage,
    retry,
    error,
    detailsItems,
    updateStatusChartItems,
    derivedOverview,
    filteredAndSortedUpdates,
    tableState,
    selectedStatuses,
    setSelectedStatuses,
    updateSearchState,
    searchState,
    hasUpdates: derivedUpdates.length > 0,
    actionsWithMissingDataByErrCode,
  };
}
